import { Middleware } from 'openapi-fetch';
import type { components } from './generated/types';

export interface AuthState {
  token: string | null;
}

export interface AuthMiddlewareHooks {
  /** Use this to explicitly set the token. This is done automatically if you invoke /authenticate. */
  setToken: (token: string) => void;
  /** Use this to get the current token. */
  getToken: () => string | null;
  /** Use this to clear the current token. */
  clearToken: () => void;
}

export interface AuthMiddlewareResponse extends AuthMiddlewareHooks {
  middleware: Middleware;
}

export function makeAuthMiddleware(authToken?: string): AuthMiddlewareResponse {
  const authState: AuthState = {
    token: authToken ?? null,
  };

  const authMiddleware: Middleware = {
    onRequest: ({ request }) => {
      if (authState.token) {
        request.headers.set('Authorization', `Bearer ${authState.token}`);
      }
      return request;
    },

    onResponse: async ({ response, schemaPath }) => {
      // Check if this is an authenticate request
      if (schemaPath === '/authenticate') {
        try {
          const data: components['schemas']['AuthenticateResponse'] =
            await response.clone().json();

          // Check if the response is successful and contains a token
          if (data.success) {
            authState.token = data.data.token;
          }
        } catch (error) {
          // Ignore parsing errors - the response might not be JSON
          console.warn('Failed to parse authenticate response:', error);
        }
      }

      return response;
    },
  };

  return {
    middleware: authMiddleware,
    setToken: (token: string) => (authState.token = token),
    getToken: () => authState.token,
    clearToken: () => (authState.token = null),
  };
}
